<template>
  <div class="terrain-attachment-lightbox">
    <header class="terrain-attachment-lightbox__header">
      <span class="terrain-attachment-lightbox__title">{{ label }}</span>
      <button type="button" class="terrain-attachment-lightbox__close" aria-label="Cerrar" @click="$emit('close')">
        <font-awesome-icon icon="times" />
      </button>
    </header>

    <template v-if="kind === 'photo'">
      <div class="terrain-attachment-lightbox__body">
        <button
          v-if="hasMultiple"
          type="button"
          class="terrain-attachment-lightbox__nav terrain-attachment-lightbox__nav--prev"
          aria-label="Foto anterior"
          @click="prev"
        >
          <font-awesome-icon icon="chevron-circle-left" />
        </button>
        <img :src="currentUrl" :alt="currentName" class="terrain-attachment-lightbox__image">
        <button
          v-if="hasMultiple"
          type="button"
          class="terrain-attachment-lightbox__nav terrain-attachment-lightbox__nav--next"
          aria-label="Foto siguiente"
          @click="next"
        >
          <font-awesome-icon icon="chevron-circle-right" />
        </button>
      </div>
      <footer class="terrain-attachment-lightbox__footer">
        <span v-if="hasMultiple" class="terrain-attachment-lightbox__counter">{{ currentIndex + 1 }} / {{ attachmentIds.length }}</span>
        <div class="terrain-attachment-lightbox__actions">
          <a :href="currentUrl" target="_blank" rel="noopener" class="terrain-attachment-lightbox__action">
            <font-awesome-icon icon="external-link-alt" /> Ver en pestaña nueva
          </a>
          <button type="button" class="terrain-attachment-lightbox__action" :disabled="downloading" @click="download(currentId, currentName)">
            <font-awesome-icon icon="download" /> {{ downloading ? 'Descargando…' : 'Descargar' }}
          </button>
        </div>
      </footer>
    </template>

    <ul v-else class="terrain-attachment-lightbox__file-list">
      <li v-for="id in attachmentIds" :key="id" class="terrain-attachment-lightbox__file-item">
        <font-awesome-icon :icon="iconFor(attachmentNames[id])" class="terrain-attachment-lightbox__file-icon" />
        <span class="terrain-attachment-lightbox__file-name">{{ attachmentNames[id] || 'Adjunto' }}</span>
        <a :href="contentUrl(id)" target="_blank" rel="noopener" class="terrain-attachment-lightbox__action" title="Ver en pestaña nueva">
          <font-awesome-icon icon="external-link-alt" />
        </a>
        <button type="button" class="terrain-attachment-lightbox__action" title="Descargar" :disabled="downloading" @click="download(id, attachmentNames[id])">
          <font-awesome-icon icon="download" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { createTerrainApi } from '@/services/terrain/api.js';
import { iconForAttachmentName } from '@/services/terrain/attachmentIcons.js';

// AttachmentLightbox es el contenido de un modal dinámico
// (this.$modal.show(AttachmentLightbox, ...), ver AttachmentGallery.vue) —
// para "photo" muestra un visor de imagen con navegación entre fotos; para
// "file" una lista de archivos con ícono por extensión. "Descargar" fuerza
// el guardado real vía blob (el <a href> normal solo abre/previsualiza,
// como corresponde ahora que el backend sirve Content-Disposition: inline
// — ver handlers/terrain/attachments.go::Download).
export default {
  name: 'AttachmentLightbox',
  props: {
    attachmentIds: { type: Array, required: true },
    attachmentNames: { type: Object, default: () => ({}) },
    kind: { type: String, required: true, validator: (value) => ['photo', 'file'].includes(value) },
    label: { type: String, default: 'Adjuntos' },
    startIndex: { type: Number, default: 0 },
  },
  data() {
    return { currentIndex: this.startIndex, downloading: false };
  },
  computed: {
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
    hasMultiple() { return this.attachmentIds.length > 1; },
    currentId() { return this.attachmentIds[this.currentIndex]; },
    currentName() { return this.attachmentNames[this.currentId] || `Foto ${this.currentIndex + 1}`; },
    currentUrl() { return this.contentUrl(this.currentId); },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    contentUrl(id) { return this.terrainApi.getAttachmentContentUrl(id); },
    iconFor(name) { return iconForAttachmentName(name); },
    prev() { this.currentIndex = (this.currentIndex - 1 + this.attachmentIds.length) % this.attachmentIds.length; },
    next() { this.currentIndex = (this.currentIndex + 1) % this.attachmentIds.length; },
    onKeydown(event) {
      if (event.key === 'Escape') this.$emit('close');
      if (this.kind === 'photo' && this.hasMultiple) {
        if (event.key === 'ArrowLeft') this.prev();
        if (event.key === 'ArrowRight') this.next();
      }
    },
    async download(id, name) {
      this.downloading = true;
      try {
        const response = await this.$http.get(this.contentUrl(id), { responseType: 'blob', withCredentials: true });
        const blobUrl = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = name || 'adjunto';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-attachment-lightbox {
  background: var(--color-white);
  border-radius: var(--terrain-radius-lg);
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100%;
  max-height: 90vh;
  overflow: hidden;
  width: 100%;
}

.terrain-attachment-lightbox__header {
  align-items: center;
  border-bottom: 1px solid var(--color-line);
  display: flex;
  justify-content: space-between;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
}
.terrain-attachment-lightbox__title { color: var(--color-navy); font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.terrain-attachment-lightbox__close {
  background: none; border: 0; color: var(--color-ghost); cursor: pointer; flex-shrink: 0; font-size: 1.1rem; padding: var(--terrain-space-xs);
  &:hover { color: var(--color-navy); }
}

.terrain-attachment-lightbox__body {
  align-items: center;
  background: var(--color-graphite, #2b3138);
  display: flex;
  justify-content: center;
  min-height: 200px;
  overflow: hidden;
  padding: var(--terrain-space-md);
  position: relative;
}
.terrain-attachment-lightbox__image { height: 100%; max-width: 100%; object-fit: contain; width: 100%; }

.terrain-attachment-lightbox__nav {
  background: rgba(255, 255, 255, .85);
  border: 0;
  border-radius: 50%;
  color: var(--color-navy);
  cursor: pointer;
  font-size: 1.4rem;
  height: 40px;
  line-height: 1;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  z-index: 1;
  &:hover { background: var(--color-white); }
}
.terrain-attachment-lightbox__nav--prev { left: var(--terrain-space-sm); }
.terrain-attachment-lightbox__nav--next { right: var(--terrain-space-sm); }

.terrain-attachment-lightbox__footer {
  align-items: center;
  border-top: 1px solid var(--color-line);
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-sm);
  justify-content: space-between;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
}
.terrain-attachment-lightbox__counter { color: var(--color-ghost); font-size: var(--terrain-font-caption); }
.terrain-attachment-lightbox__actions { display: flex; gap: var(--terrain-space-xs); }
.terrain-attachment-lightbox__action {
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  color: var(--color-navy);
  cursor: pointer;
  display: inline-flex;
  font-weight: 600;
  font-size: var(--terrain-font-caption);
  gap: var(--terrain-space-xs);
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  text-decoration: none;
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-mist); border-color: var(--color-steel); }
  &:disabled { cursor: not-allowed; opacity: .6; }
}

.terrain-attachment-lightbox__file-list { display: grid; gap: var(--terrain-space-xs); list-style: none; margin: 0; max-height: 60vh; overflow-y: auto; padding: var(--terrain-space-md); }
.terrain-attachment-lightbox__file-item {
  align-items: center;
  background: var(--color-mist);
  border-radius: var(--terrain-radius-md);
  display: flex;
  gap: var(--terrain-space-sm);
  padding: var(--terrain-space-sm);
}
.terrain-attachment-lightbox__file-icon { color: var(--color-steel); }
.terrain-attachment-lightbox__file-name { color: var(--color-navy); flex: 1; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
